import { NgStyle } from '@angular/common'
import { ChangeDetectorRef, Component, inject } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faSpinner, faX } from '@fortawesome/free-solid-svg-icons'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import imageCompression, { Options } from 'browser-image-compression'
import { Observable } from 'rxjs'

import { AutoAccidentReport, FeatureType, ListItem, RepositoryModal } from '../../shared'
import { VoiceTextFieldComponent } from '../../shared/components/voice-text-field/voice-text-field.component'
import { AutoAccidentReportsComponent } from './auto-accident-reports.component'

@Component({
    templateUrl: './auto-accident-report.component.html',
    standalone: true,
    imports: [FormsModule, WjInputModule, FaIconComponent, NgStyle, VoiceTextFieldComponent],
    styles: [
        `
            .accident-upload-label {
                position: relative;
                margin-right: auto;
                margin-bottom: 0;
            }
            .accident-upload-label.disabled {
                pointer-events: none;
                opacity: 0.65;
            }
            .accident-file-input {
                position: absolute;
                width: 1px;
                height: 1px;
                padding: 0;
                margin: -1px;
                overflow: hidden;
                clip: rect(0, 0, 0, 0);
                white-space: nowrap;
                border: 0;
            }
        `,
    ],
})
export class AutoAccidentReportComponent extends RepositoryModal<AutoAccidentReport> {
    private cdr = inject(ChangeDetectorRef)

    faX = faX
    faSpinner = faSpinner

    userList: ListItem<string>[] = []
    dotClassificationList: ListItem<string>[] = [new ListItem('DOT', 'DOT'), new ListItem('Non-DOT', 'Non-DOT')]

    attachments: any[] = []
    progress = 0

    protected onInitialized(): void {
        const parent = this.editOptions.initializeParams as AutoAccidentReportsComponent
        this.userList = parent.activeUserList ?? []

        if (this.isAdding && !this.target.reportedByUserId) {
            const currentUserId = parent.currentUserId
            if (currentUserId && this.userList.some(u => u.value === currentUserId)) {
                this.target.reportedByUserId = currentUserId
            }
        }
    }

    get canEdit(): boolean {
        return this.services.authentication.hasFeature([FeatureType.SafetyAutoAccidentReportEdit])
    }

    removeAttachment(file: any) {
        this.attachments.remove(file)
    }

    async onFileChanged(event: Event) {
        const input = event.target as HTMLInputElement
        const files = input.files ? Array.from(input.files) : []
        if (files.length === 0) return

        try {
            for (const file of files) {
                try {
                    this.attachments.push(await this.prepareAttachment(file))
                } catch {
                    this.services.notification.error(`Could not add ${file.name || 'that file'}.`)
                }
                this.cdr.detectChanges()
            }
        } finally {
            input.value = ''
            this.progress = 0
            this.cdr.detectChanges()
        }
    }

    isPdf(url: string): boolean {
        return url.toLowerCase().endsWith('.pdf')
    }

    isDocument(url: string): boolean {
        const lower = url.toLowerCase()
        return ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt'].some(ext => lower.endsWith(ext))
    }

    getExistingFileSrc(url: string): string {
        if (this.isDocument(url)) {
            return './Resources/pdfImage.png'
        }
        if (!this.isPreviewableImage(url)) {
            return ''
        }
        return './Resources/Images/AutoAccidentReport/' + url + '?w=300'
    }

    getPendingFileSrc(file: any): string {
        if (file?.type === 'application/pdf' || (file?.name && this.isDocument(file.name))) {
            return './Resources/pdfImage.png'
        }
        if (file?.url && this.isPreviewableImage(file.name)) {
            return file.url
        }
        return ''
    }

    displayFileName(index: number): string {
        return this.target.imageFileNames?.[index] ?? 'Attachment'
    }

    openExistingFile(url: string) {
        if (this.isDocument(url)) {
            window.open('./Resources/Images/AutoAccidentReport/' + url, '_blank')
        } else {
            window.open('./Resources/Images/AutoAccidentReport/' + url, '_blank')
        }
    }

    private uploadAttachments(id: string): Observable<any> {
        const uploadData = new FormData()
        uploadData.append('id', id)
        for (let i = 0; i < this.attachments.length; i++) {
            const file = this.withSafeName(this.attachments[i])
            this.attachments[i] = file
            uploadData.append('myFile' + i, file, file.name)
        }
        return this.services.data.post('api/AutoAccidentReport/UploadImages', uploadData)
    }

    private async prepareAttachment(file: File): Promise<File> {
        const source = await this.convertHeicIfPossible(file)
        if (!this.isCompressibleImage(source)) {
            return this.withPreview(source)
        }

        try {
            const options: Options = {
                maxSizeMB: 2,
                maxWidthOrHeight: 1920,
                useWebWorker: true,
                onProgress: percent => {
                    this.progress = percent
                    this.cdr.detectChanges()
                },
            }
            const compressed = await imageCompression(source, options)
            return this.withPreview(compressed, compressed.name || source.name)
        } catch {
            return this.withPreview(source)
        }
    }

    private async convertHeicIfPossible(file: File): Promise<File> {
        if (!this.isHeic(file) || typeof createImageBitmap !== 'function') return file

        try {
            const bitmap = await createImageBitmap(file)
            try {
                const maxEdge = 1920
                const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height))
                const width = Math.max(1, Math.round(bitmap.width * scale))
                const height = Math.max(1, Math.round(bitmap.height * scale))
                const canvas = document.createElement('canvas')
                canvas.width = width
                canvas.height = height
                const context = canvas.getContext('2d')
                if (!context) return file
                context.drawImage(bitmap, 0, 0, width, height)
                const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(result => resolve(result), 'image/jpeg', 0.9))
                if (!blob) return file
                return new File([blob], `${this.fileBaseName(file.name)}.jpg`, { type: 'image/jpeg', lastModified: file.lastModified })
            } finally {
                bitmap.close()
            }
        } catch {
            return file
        }
    }

    private async withPreview(file: File, preferredName?: string): Promise<File> {
        const named = this.withSafeName(file, preferredName)
        const preview = named as File & { url?: string }
        if (this.isDocument(named.name)) {
            preview.url = './Resources/pdfImage.png'
            return preview
        }
        if (!this.isPreviewableImage(named.name)) {
            preview.url = ''
            return preview
        }
        try {
            preview.url = await imageCompression.getDataUrlFromFile(named)
        } catch {
            preview.url = ''
        }
        return preview
    }

    private withSafeName(file: File, preferredName?: string): File {
        const safe = this.safeFileName(preferredName || file.name, file.type)
        if (file.name === safe) return file
        const named = new File([file], safe, { type: file.type || 'application/octet-stream', lastModified: file.lastModified })
        const previewUrl = (file as File & { url?: string }).url
        if (previewUrl) (named as File & { url?: string }).url = previewUrl
        return named
    }

    private safeFileName(name: string, type?: string): string {
        const ext = this.safeExtension(name, type)
        const base = this.fileBaseName(name)
        const maxBase = Math.max(1, 200 - ext.length)
        return base.slice(0, maxBase) + ext
    }

    private fileBaseName(name: string): string {
        const cleaned = (name || '').replace(/[\\/:*?"<>|;\u0000-\u001f]/g, ' ').replace(/\s+/g, ' ').trim()
        const dot = cleaned.lastIndexOf('.')
        const base = (dot > 0 ? cleaned.slice(0, dot) : cleaned).replace(/[. ]+$/g, '')
        return base || 'attachment'
    }

    private safeExtension(name: string, type?: string): string {
        const cleaned = (name || '').toLowerCase()
        const dot = cleaned.lastIndexOf('.')
        let ext = dot > 0 ? cleaned.slice(dot) : ''
        if (!/^\.[a-z0-9]{1,9}$/.test(ext)) {
            ext = this.extensionForType(type)
        }
        return ext.slice(0, 10)
    }

    private extensionForType(type?: string): string {
        switch ((type || '').toLowerCase()) {
            case 'image/jpeg':
                return '.jpg'
            case 'image/png':
                return '.png'
            case 'image/gif':
                return '.gif'
            case 'image/webp':
                return '.webp'
            case 'image/bmp':
                return '.bmp'
            case 'image/heic':
            case 'image/heic-sequence':
                return '.heic'
            case 'image/heif':
            case 'image/heif-sequence':
                return '.heif'
            case 'application/pdf':
                return '.pdf'
            default:
                return ''
        }
    }

    private isHeic(file: File): boolean {
        const type = (file.type || '').toLowerCase()
        const name = (file.name || '').toLowerCase()
        return type.includes('heic') || type.includes('heif') || name.endsWith('.heic') || name.endsWith('.heif')
    }

    private isCompressibleImage(file: File): boolean {
        if (this.isHeic(file)) return false
        const type = (file.type || '').toLowerCase()
        if (type.startsWith('image/')) return true
        return this.isPreviewableImage(file.name)
    }

    private isPreviewableImage(name: string): boolean {
        const lower = (name || '').toLowerCase()
        return ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'].some(ext => lower.endsWith(ext))
    }

    protected onSave(resolveSave: (result?: any) => void, rejectSave: (reason?: any) => void) {
        const observable = this.isAdding || this.isDuplicating ? this.repositoryAdd() : this.repositoryEdit()
        observable.subscribe({
            next: successResult => {
                if (this.attachments.length === 0) {
                    resolveSave(successResult)
                    return
                }

                this.uploadAttachments(successResult.id).subscribe({
                    next: () => resolveSave(successResult),
                    error: errorResult => rejectSave(errorResult),
                })
            },
            error: errorResult => rejectSave(errorResult),
        })
    }
}
